import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { getCR, saveCR, getDossier } from '../services/api';
import { saveDraft, loadDraft, removeDraft, saveDraftSync } from '../services/draftStorage';

function compressImage(dataUrl, maxPx = 1200, quality = 0.75) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxPx || h > maxPx) {
        if (w > h) { h = Math.round(h * maxPx / w); w = maxPx; }
        else { w = Math.round(w * maxPx / h); h = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = dataUrl;
  });
}

const PhotoSection = React.memo(function PhotoSection({ label, photos, onChange, max = 0, hint = '' }) {
  const [compressing, setCompressing] = useState(false);
  const handleAdd = async e => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    setCompressing(true);
    try {
      for (const file of files) {
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async ev => {
            try {
              const compressed = await compressImage(ev.target.result);
              onChange(prev => [...prev, compressed]);
            } catch {}
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
    } finally {
      setCompressing(false);
    }
  };
  const remove = i => onChange(photos.filter((_, idx) => idx !== i));
  return (
    <div className="field">
      <div className="field-label">{label} ({photos.length}){hint && <span style={{ color: 'var(--texte-doux)', fontWeight: 400, fontSize: 11, marginLeft: 6 }}>{hint}</span>}{compressing && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--attention-texte)' }}>⏳ Compression…</span>}</div>
      <div className="photo-grid">
        {photos.map((p, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <img src={p} alt="" className="photo-thumb" />
            <div onClick={() => remove(i)} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, cursor: 'pointer' }}>✕</div>
          </div>
        ))}
        {(max === 0 || photos.length < max) && !compressing && (
          <>
            <label className="photo-add" style={{ flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 22 }}>📷</span>
              <span style={{ fontSize: 10, color: 'var(--texte-doux)' }}>Photo</span>
              <input type="file" accept="image/*" capture="environment" onChange={handleAdd} style={{ display: 'none' }} />
            </label>
            <label className="photo-add" style={{ flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 22 }}>🖼️</span>
              <span style={{ fontSize: 10, color: 'var(--texte-doux)' }}>Galerie</span>
              <input type="file" accept="image/*" onChange={handleAdd} style={{ display: 'none' }} multiple />
            </label>
          </>
        )}
      </div>
    </div>
  );
});

function Toggle({ label, value, onChange, hint = '' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '0.5px solid var(--bord)' }}>
      <div>
        <span style={{ fontSize: 14, color: 'var(--texte)' }}>{label}</span>
        {hint && <div style={{ fontSize: 11, color: 'var(--texte-doux)', marginTop: 2 }}>{hint}</div>}
      </div>
      <div onClick={() => onChange(!value)} style={{ width: 44, height: 26, borderRadius: 13, background: value ? 'var(--accent)' : 'var(--bord-fort)', position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0, marginLeft: 12 }}>
        <div style={{ position: 'absolute', top: 3, left: value ? 21 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder = '', hint = '' }) {
  return (
    <div className="field">
      <div className="field-label">{label}{hint && <span style={{ color: 'var(--texte-doux)', fontWeight: 400, fontSize: 11, marginLeft: 6 }}>{hint}</span>}</div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Textarea({ label, value, onChange, placeholder = '', minHeight = 80 }) {
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)', minHeight, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
    </div>
  );
}

function SectionTitle({ num, children, color = '#1a3a6b' }) {
  return (
    <div style={{ background: color, borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 700, color: '#fff', margin: '22px 0 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
      {num && <span style={{ background: 'rgba(255,255,255,0.25)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 }}>{num}</span>}
      {children}
    </div>
  );
}

function InfoCard({ children }) {
  return <div style={{ background: 'var(--surface-3)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{children}</div>;
}

function GPSButton({ label, value, onChange }) {
  const [loading, setLoading] = useState(false);
  const get = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => { onChange(`${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`); setLoading(false); },
      () => { alert('GPS non disponible'); setLoading(false); }
    );
  };
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder="Latitude, Longitude"
          style={{ flex: 1, padding: '10px 12px', border: '0.5px solid var(--bord-fort)', borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
        <button onClick={get} style={{ padding: '10px 14px', background: 'var(--accent-plein)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {loading ? '...' : '📍 GPS'}
        </button>
      </div>
    </div>
  );
}

export default function CompteRenduVT() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sigRef = useRef(null);

  const [dossier, setDossier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [offlineRestored, setOfflineRestored] = useState(false);

  // PM
  const [refPM, setRefPM] = useState('');
  const [adressePM, setAdressePM] = useState('');
  const [ropConforme, setRopConforme] = useState(true);
  const [coupleurPresent, setCoupleurPresent] = useState(false);
  const [photosPM, setPhotosPM] = useState([]);

  // PBO
  const [refPBO, setRefPBO] = useState('');
  const [adressePBO, setAdressePBO] = useState('');
  const [typePBO, setTypePBO] = useState('Chambre souterraine (RAC_C)');
  const [cheminPBOPTO, setCheminPBOPTO] = useState('Souterrain');
  const [photosPBO, setPhotosPBO] = useState([]);

  // Cheminements extérieurs
  const [photosExtPBO, setPhotosExtPBO] = useState([]);
  const [photosExtPenetration, setPhotosExtPenetration] = useState([]);

  // Cheminements intérieurs
  const [adductionClient, setAdductionClient] = useState('');
  const [travauxClient, setTravauxClient] = useState(false);
  const [descTravauxClient, setDescTravauxClient] = useState('');
  const [photosInt, setPhotosInt] = useState([]);

  // Estimation câblage
  const [lgPublic, setLgPublic] = useState('');
  const [lgPrive, setLgPrive] = useState('');
  const [lgFauxPlafond, setLgFauxPlafond] = useState('');
  const [nbPercemements, setNbPercemements] = useState('');
  const [nbGoulottes, setNbGoulottes] = useState('');
  const [hydrocurage, setHydrocurage] = useState(false);
  const [nacelle, setNacelle] = useState(false);
  const [hno, setHno] = useState(false);
  const [binome, setBinome] = useState(false);
  const [aiguillage, setAiguillage] = useState(false);
  const [nbHeures, setNbHeures] = useState('');
  const [devis, setDevis] = useState(false);

  // VT Complexe
  const [vtComplexe, setVtComplexe] = useState(false);
  const [typeAccesPBO, setTypeAccesPBO] = useState('Libre');
  const [chambres, setChambres] = useState([{ id: 1, gps: '', photos: [], photosOuverte: [] }]);
  const [photosPlanPublic, setPhotosPlanPublic] = useState([]);
  const [photosPlanPrive, setPhotosPlanPrive] = useState([]);

  // Conclusion
  const [commentaireIntervenant, setCommentaireIntervenant] = useState('');
  const [photosFacade, setPhotosFacade] = useState([]);
  const [signatureTech, setSignatureTech] = useState('');
  const [signedTech, setSignedTech] = useState(false);
  const [nomTechnicien, setNomTechnicien] = useState(() => {
    try { const u = JSON.parse(localStorage.getItem('user')); return u ? `${u.prenom || ''} ${u.nom || ''}`.trim() : ''; } catch { return ''; }
  });
  const [dechargeKosc, setDechargeKosc] = useState('');

  const lgTotal = (parseFloat(lgPublic) || 0) + (parseFloat(lgPrive) || 0);

  const LOCAL_KEY = `cr_draft_${id}`;

  const saveLocalDraft = (payload, doss) => {
    saveDraft(LOCAL_KEY, payload, doss).catch(() => {});
  };

  const applyDraftData = (cr) => {
    if (cr.ref_pm) setRefPM(cr.ref_pm);
    if (cr.adresse_pm) setAdressePM(cr.adresse_pm);
    if (cr.rop_conforme !== undefined) setRopConforme(cr.rop_conforme);
    if (cr.coupleur_present !== undefined) setCoupleurPresent(cr.coupleur_present);
    if (cr.photos_pm) setPhotosPM(cr.photos_pm);
    if (cr.ref_pbo) setRefPBO(cr.ref_pbo);
    if (cr.adresse_pbo) setAdressePBO(cr.adresse_pbo);
    if (cr.type_pbo) {
      const legacyMap = {
        'Chambre souterraine': 'Chambre souterraine (RAC_C)',
        'Aérien poteau Orange': 'Aérien poteau (RAC_P)',
        'Aérien poteau propre': 'Aérien poteau (RAC_P)',
        'Local technique': 'Local technique immeuble (RAC_I)',
        'Façade immeuble': 'Façade immeuble (RAC_F)',
      };
      setTypePBO(legacyMap[cr.type_pbo] || cr.type_pbo);
    }
    if (cr.chemin_pbo_pto) setCheminPBOPTO(cr.chemin_pbo_pto);
    if (cr.photos_pbo) setPhotosPBO(cr.photos_pbo);
    if (cr.photos_ext_pbo) setPhotosExtPBO(cr.photos_ext_pbo);
    if (cr.photos_ext_penetration) setPhotosExtPenetration(cr.photos_ext_penetration);
    if (cr.adduction_client) setAdductionClient(cr.adduction_client);
    if (cr.travaux_client !== undefined) setTravauxClient(cr.travaux_client);
    if (cr.desc_travaux_client) setDescTravauxClient(cr.desc_travaux_client);
    if (cr.photos_int) setPhotosInt(cr.photos_int);
    if (cr.lg_public) setLgPublic(cr.lg_public);
    if (cr.lg_prive) setLgPrive(cr.lg_prive);
    if (cr.lg_faux_plafond) setLgFauxPlafond(cr.lg_faux_plafond);
    if (cr.nb_percemements) setNbPercemements(cr.nb_percemements);
    if (cr.nb_goulottes) setNbGoulottes(cr.nb_goulottes);
    if (cr.hydrocurage !== undefined) setHydrocurage(cr.hydrocurage);
    if (cr.nacelle !== undefined) setNacelle(cr.nacelle);
    if (cr.hno !== undefined) setHno(cr.hno);
    if (cr.binome !== undefined) setBinome(cr.binome);
    if (cr.aiguillage !== undefined) setAiguillage(cr.aiguillage);
    if (cr.nb_heures) setNbHeures(cr.nb_heures);
    if (cr.devis !== undefined) setDevis(cr.devis);
    if (cr.vt_complexe !== undefined) setVtComplexe(cr.vt_complexe);
    if (cr.type_acces_pbo) setTypeAccesPBO(cr.type_acces_pbo);
    if (cr.chambres && cr.chambres.length > 0) setChambres(cr.chambres);
    if (cr.photos_plan_public) setPhotosPlanPublic(cr.photos_plan_public);
    if (cr.photos_plan_prive) setPhotosPlanPrive(cr.photos_plan_prive);
    if (cr.commentaire_intervenant) setCommentaireIntervenant(cr.commentaire_intervenant);
    if (cr.photos_facade) setPhotosFacade(cr.photos_facade);
    if (cr.nom_technicien) setNomTechnicien(cr.nom_technicien);
    if (cr.decharge_kosc) setDechargeKosc(cr.decharge_kosc);
    if (cr.signature_tech) { setSignatureTech(cr.signature_tech); setSignedTech(true); }
    if (cr.pdf_url) setPdfUrl(cr.pdf_url);
  };

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      const localDraft = await loadDraft(LOCAL_KEY).catch(() => null);
      try {
        const [dData, crData] = await Promise.all([getDossier(id), getCR(id)]);
        setDossier(dData.dossier);
        if (dData.dossier) {
          setRefPM(dData.dossier.ref_pm || '');
          setAdressePM(dData.dossier.adresse_pm || '');
          setRefPBO(dData.dossier.ref_pt_pbo || '');
          setAdressePBO(dData.dossier.adresse_pbo || '');
        }
        const cr = crData.cr;
        const localTs = localDraft?._ts || 0;
        const serverTs = cr?._ts || 0;
        if (localTs > serverTs + 60000 && localDraft?.cr) {
          applyDraftData(localDraft.cr);
          setOfflineRestored(true);
        } else if (cr) {
          applyDraftData(cr);
          saveLocalDraft(cr, null);
        } else if (localDraft?.cr) {
          applyDraftData(localDraft.cr);
          setOfflineRestored(true);
        }
      } catch {
        if (localDraft) {
          if (localDraft.dossier) setDossier(localDraft.dossier);
          if (localDraft.cr) applyDraftData(localDraft.cr);
          setOfflineRestored(true);
        }
      }
    };
    load();
  }, [id]);

  // Détection connexion
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const captureSignature = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      setSignatureTech(sigRef.current.toDataURL('image/png'));
      setSignedTech(true);
    }
  };

  const addChambre = () => setChambres(prev => [...prev, { id: prev.length + 1, gps: '', photos: [], photosOuverte: [] }]);
  const updateChambre = (idx, key, val) => setChambres(prev => prev.map((c, i) => i === idx ? { ...c, [key]: val } : c));

  const buildPayload = (final) => ({
    dossier_id: id, final, type_cr: 'VT',
    ref_pm: refPM, adresse_pm: adressePM, rop_conforme: ropConforme,
    coupleur_present: coupleurPresent, photos_pm: photosPM,
    ref_pbo: refPBO, adresse_pbo: adressePBO, type_pbo: typePBO,
    type_raccordement: { 'Chambre souterraine (RAC_C)': 'RAC_C', 'Aérien poteau (RAC_P)': 'RAC_P', 'Façade immeuble (RAC_F)': 'RAC_F', 'Local technique immeuble (RAC_I)': 'RAC_I' }[typePBO] || '',
    chemin_pbo_pto: cheminPBOPTO, photos_pbo: photosPBO,
    photos_ext_pbo: photosExtPBO, photos_ext_penetration: photosExtPenetration,
    adduction_client: adductionClient, travaux_client: travauxClient,
    desc_travaux_client: descTravauxClient, photos_int: photosInt,
    lg_public: lgPublic, lg_prive: lgPrive, lg_total: lgTotal,
    lg_faux_plafond: lgFauxPlafond, nb_percemements: nbPercemements,
    nb_goulottes: nbGoulottes, hydrocurage, nacelle, hno, binome,
    aiguillage, nb_heures: nbHeures, devis,
    vt_complexe: vtComplexe, type_acces_pbo: typeAccesPBO,
    chambres, photos_plan_public: photosPlanPublic, photos_plan_prive: photosPlanPrive,
    commentaire_intervenant: commentaireIntervenant,
    photos_facade: photosFacade, signature_tech: signatureTech,
    nom_technicien: nomTechnicien,
    decharge_kosc: dechargeKosc,
  });

  // Auto-save quand l'app passe en arrière-plan
  const autoSaveRef = useRef(null);
  autoSaveRef.current = { dossier, generating, pdfUrl, buildPayload, saveLocalDraft, LOCAL_KEY };
  useEffect(() => {
    const handleHide = () => {
      if (!document.hidden) return;
      const s = autoSaveRef.current;
      if (s && s.dossier && !s.generating && !s.pdfUrl) {
        const payload = s.buildPayload(false);
        saveDraftSync(s.LOCAL_KEY, payload, s.dossier);
        s.saveLocalDraft(payload, s.dossier);
        try { saveCR(payload); } catch {}
      }
    };
    document.addEventListener('visibilitychange', handleHide);
    return () => document.removeEventListener('visibilitychange', handleHide);
  }, []);

  // Sauvegarde locale périodique toutes les 30s
  useEffect(() => {
    const timer = setInterval(() => {
      const s = autoSaveRef.current;
      if (s && s.dossier && !s.generating && !s.pdfUrl) {
        s.saveLocalDraft(s.buildPayload(false), s.dossier);
      }
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const handleSaveDraft = async () => {
    setSaving(true); setMessage('');
    const payload = buildPayload(false);
    const localOk = await saveDraft(LOCAL_KEY, payload, dossier).catch(() => false);
    const localMsg = localOk === true    ? '✓ Sauvegardé localement (photos incluses)'
                   : localOk === 'fallback' ? '⚠ Sauvegardé sans photos (iPhone — stockage limité). Revenez vite !'
                   : '⚠ Erreur sauvegarde locale — vérifiez l\'espace disponible';
    if (!isOnline) {
      setSaving(false);
      setMessage(localMsg);
      return;
    }
    try { await saveCR(payload); setMessage(localOk ? '✓ Brouillon sauvegardé (local + serveur)' : '✓ Brouillon sauvegardé sur le serveur'); }
    catch { setMessage(localOk ? '✓ Sauvegardé localement — erreur serveur' : 'Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleBack = async () => {
    if (dossier && !generating && !pdfUrl) {
      const payload = buildPayload(false);
      saveLocalDraft(payload, dossier);
      try { await saveCR(payload); } catch {}
    }
    navigate(`/dossier/${id}`);
  };

  const handleGenerate = async () => {
    if (!isOnline) {
      setMessage('Impossible de générer le PDF hors ligne. Reconnectez-vous puis réessayez.');
      return;
    }
    if (!dechargeKosc.trim()) {
      setMessage('Le N° de décharge KOSC est obligatoire');
      return;
    }
    setGenerating(true); setMessage('');
    try {
      const data = await saveCR(buildPayload(true));
      setPdfUrl(data.pdf_url);
      setMessage('CR VT généré avec succès !');
      removeDraft(LOCAL_KEY).catch(() => {});
    } catch { setMessage('Erreur lors de la génération du PDF'); }
    finally { setGenerating(false); }
  };

  if (!dossier) return (
    <div className="app-shell">
      <div className="topbar"><div className="topbar-title">Chargement…</div></div>
    </div>
  );

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="back-btn" onClick={handleBack}>‹</div>
        <div className="topbar-title">Visite Technique</div>
        <div style={{ fontSize: 11, color: 'var(--texte-doux)' }}>{dossier.ref_commande}</div>
      </div>

      {!isOnline && (
        <div style={{ background: 'var(--attention-fond)', color: 'var(--attention-texte)', padding: '8px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center', borderBottom: '1px solid var(--attention-bord)' }}>
          Hors ligne — données sauvegardées localement
        </div>
      )}
      {offlineRestored && (
        <div style={{ background: 'var(--succes-fond)', color: 'var(--succes-texte)', padding: '8px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center', borderBottom: '1px solid var(--succes-bord)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Données restaurées depuis la sauvegarde locale</span>
          <span style={{ cursor: 'pointer', fontWeight: 400 }} onClick={() => setOfflineRestored(false)}>✕</span>
        </div>
      )}

      <div className="content">

        {/* Résumé dossier */}
        <InfoCard>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{dossier.client_final}</div>
          <div style={{ color: 'var(--texte-doux)', fontSize: 12 }}>{dossier.numero} {dossier.rue}, {dossier.code_postal} {dossier.ville}</div>
          <div style={{ color: 'var(--texte-doux)', fontSize: 12, marginTop: 2 }}>Date : {dossier.date_intervention} à {dossier.heure_intervention}</div>
          <div style={{ color: 'var(--texte-doux)', fontSize: 12 }}>Opérateur : {dossier.client_operateur} — {dossier.offre}</div>
        </InfoCard>

        {/* ===== 1 — PM ===== */}
        <SectionTitle num="1">Informations Point de Mutualisation (PM)</SectionTitle>

        <Input label="Référence du PM" value={refPM} onChange={setRefPM} placeholder="Ex: PT 000450" />
        <Input label="Adresse du PM" value={adressePM} onChange={setAdressePM} />
        <Toggle label="La route optique est-elle conforme ?" value={ropConforme} onChange={setRopConforme} />
        <Toggle label="En cas de ligne passive, le coupleur Covage est-il présent ?" value={coupleurPresent} onChange={setCoupleurPresent} />
        <PhotoSection label="Photo PM" photos={photosPM} onChange={setPhotosPM} max={3} />

        {/* ===== 2 — PBO ===== */}
        <SectionTitle num="2">Informations Point de Branchement (PBO)</SectionTitle>

        <Input label="Référence du PBO" value={refPBO} onChange={setRefPBO} placeholder="Ex: PT 000699" />
        <Input label="Adresse du PBO" value={adressePBO} onChange={setAdressePBO} />
        <Select label="Type de PBO" value={typePBO} onChange={setTypePBO} options={['Chambre souterraine (RAC_C)', 'Aérien poteau (RAC_P)', 'Façade immeuble (RAC_F)', 'Local technique immeuble (RAC_I)']} />
        <Select label="Localiser le chemin de câble PBO → PTO" value={cheminPBOPTO} onChange={setCheminPBOPTO} options={['Souterrain', 'Aérien', 'Façade', 'Mixte (aérien + souterrain)', 'Mixte (façade + souterrain)', 'Indéterminé']} />
        <PhotoSection label="Photo PBO" photos={photosPBO} onChange={setPhotosPBO} max={3} />

        {/* ===== 3 — Cheminements extérieurs ===== */}
        <SectionTitle num="3">Cheminement extérieurs au client jusqu'au PBO</SectionTitle>

        <PhotoSection
          label="Photos cheminements extérieur"
          hint="sans limite"
          photos={photosExtPBO}
          onChange={setPhotosExtPBO}
        />
        <PhotoSection
          label="Photo des chambres/poteaux empruntés"
          photos={photosExtPenetration}
          onChange={setPhotosExtPenetration}
        />

        {/* ===== 4 — Cheminements intérieurs ===== */}
        <SectionTitle num="4">Cheminements intérieurs au client</SectionTitle>

        <Input label="Adduction client localisée" value={adductionClient} onChange={setAdductionClient} placeholder="Ex: Façade, local technique RDC..." />
        <Toggle label="Faut-il des travaux client ?" value={travauxClient} onChange={setTravauxClient} />
        {travauxClient && (
          <Textarea
            label="Si oui, quels types de travaux doit réaliser le client ?"
            value={descTravauxClient}
            onChange={setDescTravauxClient}
            placeholder="Décrire en détail les travaux nécessaires côté client..."
            minHeight={100}
          />
        )}
        <PhotoSection label="Photos cheminements intérieurs" photos={photosInt} onChange={setPhotosInt} hint="sans limite" />

        {/* ===== 5 — Estimation câblage ===== */}
        <SectionTitle num="5">Estimation câblage</SectionTitle>

        <Input label="Longueur câble domaine public (ml)" value={lgPublic} onChange={setLgPublic} type="number" placeholder="Ex: 115" />
        <Input label="Longueur câble domaine privé + faux plafond (ml)" value={lgPrive} onChange={setLgPrive} type="number" placeholder="Ex: 60" />
        <InfoCard><strong>Longueur totale estimée : {lgTotal} ml</strong></InfoCard>
        <Input label="Tirage faux plafond (ml)" value={lgFauxPlafond} onChange={setLgFauxPlafond} type="number" />
        <Input label="Nombre de percements" value={nbPercemements} onChange={setNbPercemements} type="number" />
        <Input label="Nombre de ml goulottes" value={nbGoulottes} onChange={setNbGoulottes} type="number" />
        <Input label="Nombre d'heures estimées pour l'intervention" value={nbHeures} onChange={setNbHeures} type="number" placeholder="Ex: 3" />

        <div style={{ height: 8 }} />
        <Toggle label="Hydrocurage nécessaire" value={hydrocurage} onChange={setHydrocurage} />
        <Toggle label="Nacelle nécessaire" value={nacelle} onChange={setNacelle} />
        <Toggle label="Intervention de nuit (HNO)" value={hno} onChange={setHno} />
        <Toggle label="Technicien supplémentaire / Binôme" value={binome} onChange={setBinome} />
        <Toggle label="Aiguillage nécessaire" value={aiguillage} onChange={setAiguillage} />
        <Toggle label="Devis à réaliser" value={devis} onChange={setDevis} />

        {/* ===== 6 — VT Complexe ===== */}
        <SectionTitle num="6" color="#8B1A1A">VT Complexe (si applicable)</SectionTitle>

        <div style={{ background: 'var(--danger-fond)', border: '1px solid var(--danger)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: 'var(--danger-texte)' }}>
          À cocher uniquement si le PBO n'est pas à proximité directe de l'adduction client
        </div>

        <Toggle label="Visite technique complexe" value={vtComplexe} onChange={setVtComplexe} />

        {vtComplexe && (
          <>
            <Select
              label="Type d'accès PBO"
              value={typeAccesPBO}
              onChange={setTypeAccesPBO}
              options={['Libre', 'Clé barillet', 'Badge', 'Interphone', 'Contact client requis', 'Intervention commune OI']}
            />

            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-fonce)', marginBottom: 8 }}>Chambres en domaine public</div>
              {chambres.map((chambre, idx) => (
                <div key={chambre.id} style={{ background: 'var(--surface-2)', borderRadius: 10, padding: '12px 14px', marginBottom: 10, border: '0.5px solid var(--bord-fort)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--accent)' }}>Chambre #{chambre.id}</div>
                  <PhotoSection
                    label="Photo dans son environnement"
                    photos={chambre.photos}
                    onChange={val => updateChambre(idx, 'photos', typeof val === 'function' ? val(chambre.photos) : val)}
                    max={3}
                  />
                  <PhotoSection
                    label="Photo ouverte avec aiguille"
                    photos={chambre.photosOuverte}
                    onChange={val => updateChambre(idx, 'photosOuverte', typeof val === 'function' ? val(chambre.photosOuverte) : val)}
                    max={3}
                  />
                  <GPSButton
                    label="Position GPS"
                    value={chambre.gps}
                    onChange={val => updateChambre(idx, 'gps', val)}
                  />
                </div>
              ))}
              <button onClick={addChambre} style={{ width: '100%', padding: '10px', background: 'var(--info-fond)', color: 'var(--accent)', border: '1.5px dashed var(--accent)', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 12 }}>
                + Ajouter une chambre
              </button>
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-fonce)', marginBottom: 8 }}>Plan du cheminement vers le local technique</div>
            <PhotoSection label="Domaine public (photos + plan)" photos={photosPlanPublic} onChange={setPhotosPlanPublic} />
            <PhotoSection label="Domaine privé" photos={photosPlanPrive} onChange={setPhotosPlanPrive} />
          </>
        )}

        {/* ===== 7 — Conclusion ===== */}
        <SectionTitle num="7">Conclusion de l'intervention</SectionTitle>

        <PhotoSection
          label="Photo façade du site"
          hint="obligatoire"
          photos={photosFacade}
          onChange={setPhotosFacade}
          max={3}
        />

        <Textarea
          label="Commentaire intervenant"
          value={commentaireIntervenant}
          onChange={setCommentaireIntervenant}
          placeholder="Décrire les observations, contraintes identifiées, recommandations pour l'intervention..."
          minHeight={120}
        />

        <Input label="Nom du technicien" value={nomTechnicien} onChange={setNomTechnicien} />

        <div className="field">
          <div className="field-label">N° de décharge KOSC <span style={{ color: 'var(--danger)' }}>*</span></div>
          <input type="text" value={dechargeKosc} onChange={e => setDechargeKosc(e.target.value)} placeholder="Ex: D260401_15424"
            style={{ width: '100%', padding: '10px 12px', border: `0.5px solid ${dechargeKosc ? 'var(--succes-bord)' : 'var(--bord-fort)'}`, borderRadius: 10, fontSize: 14, background: 'var(--surface-2)', color: 'var(--texte)' }} />
          {!dechargeKosc && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>Obligatoire pour générer le CR</div>}
        </div>

        <div className="field">
          <div className="field-label">Signature technicien</div>
          {signedTech ? (
            <div>
              <div style={{ border: '0.5px solid var(--accent)', borderRadius: 10, background: 'var(--info-fond)', padding: 8 }}>
                <img src={signatureTech} alt="signature" style={{ maxHeight: 80 }} />
              </div>
              <div style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textAlign: 'right', padding: '4px 0' }} onClick={() => { sigRef.current?.clear(); setSignatureTech(''); setSignedTech(false); }}>
                Effacer et recommencer
              </div>
            </div>
          ) : (
            <div>
              <div className="sign-canvas-wrap">
                <SignatureCanvas ref={sigRef} canvasProps={{ width: 340, height: 120, style: { display: 'block', width: '100%', height: 120 } }} onEnd={captureSignature} />
              </div>
              <div style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textAlign: 'right', padding: '4px 0' }} onClick={() => sigRef.current?.clear()}>Effacer</div>
            </div>
          )}
        </div>

        {message && (
          <div style={{ padding: '10px 12px', borderRadius: 10, background: message.includes('succès') || message.includes('sauvegardé') ? 'var(--succes-fond)' : 'var(--danger-fond)', color: message.includes('succès') || message.includes('sauvegardé') ? 'var(--succes-texte)' : 'var(--danger-texte)', fontSize: 13, marginBottom: 10 }}>
            {message}
          </div>
        )}

        {pdfUrl && (
          <button onClick={() => {
            const token = localStorage.getItem('token');
            fetch(pdfUrl, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob())
              .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = pdfUrl.split('/').pop() || 'CR_VT.pdf';
                a.click();
                URL.revokeObjectURL(url);
              });
          }} style={{ display: 'block', width: '100%', textAlign: 'center', padding: 12, borderRadius: 12, background: 'var(--accent-fonce)', color: '#fff', fontWeight: 600, marginBottom: 8, fontSize: 15, cursor: 'pointer' }}>
            Télécharger le CR VT
          </button>
        )}

        <button className="btn btn-primary" style={{ background: 'var(--accent-fonce)' }} onClick={handleGenerate} disabled={generating}>
          {generating ? 'Génération PDF…' : 'Générer le CR Visite Technique'}
        </button>
        <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}>
          {saving ? 'Sauvegarde…' : 'Sauvegarder brouillon'}
        </button>
        <div style={{ height: 16 }} />
      </div>

      <div className="bottomnav">
        <div className="nav-item" onClick={() => navigate('/planning')}><span className="nav-icon">📅</span>Planning</div>
        <div className="nav-item" onClick={handleBack}><span className="nav-icon">📁</span>Dossier</div>
        <div className="nav-item active"><span className="nav-icon">📝</span>VT</div>
      </div>
    </div>
  );
}
